import React from 'react';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import pathfinder from '@fiverr-private/pathfinder';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Textarea } from '@fiverr-private/forms';
import { Typography } from '@fiverr-private/typography';
import { translate } from '@fiverr-private/i18n-react';
import { Button, IconButton, GoArrowRightIcon, GoTimesIcon } from '@fiverr-private/go_shared_ui';
import { createClickedElement, EVENT_TYPES } from '../../../../utils/trackGig';
import { BasicAiGigCardProps } from './BasicAiGigCard.types';

import { blackOverlay, basicAiGigCardDetails, promptTextarea } from './BasicAiGigCard.ve.css';

const BasicAiGigCard: React.FC<BasicAiGigCardProps> = ({
    sellerInfoProps,
    models,
    trackEvent,
    isBlackComponentOpen,
    setBlackComponentOpen,
}) => {
    const [prompt, setPrompt] = React.useState('');

    if (isEmpty(models) || !isBlackComponentOpen) {
        return null;
    }

    const model = models[0];
    const title = translate('gig_listings.basic_ai_card.ai_model.title');
    const description = translate('gig_listings.basic_ai_card.ai_model.description');
    const cta = translate('gig_listings.basic_ai_card.ai_model.cta');
    const promptPlaceholder = translate('gig_listings.basic_ai_card.ai_model.placeholder');

    const sendBiClickEvent = () => {
        const elementName = 'ai_model_start_generating';
        const clickedElement = createClickedElement(elementName);
        trackEvent(EVENT_TYPES.GIG_CARD_CLICK, clickedElement);
    };

    const buttonHref = pathfinder(
        'ai_hub_playground_create_tab_new_model_conversation',
        {
            seller_username: sellerInfoProps?.name,
            model_slug: model?.id || '',
        },
        {
            query: {
                ...(prompt && { prompt }),
                source: 'listings',
            },
        }
    );

    return (
        <Stack
            className={classNames(blackOverlay, { 'open-overlay': isBlackComponentOpen })}
            backgroundColor="black"
            position="absolute"
            top={0}
            left={0}
            zIndex="2"
            direction="column"
            borderRadius="2xl"
        >
            <Container className={basicAiGigCardDetails} direction="column" gap="4">
                <Stack direction="column" gap="1">
                    <Stack justifyContent="spaceBetween">
                        <Typography color="white" fontWeight="bold" size="b_lg">
                            {title}
                        </Typography>
                        <IconButton variant="tertiary" size="sm" onClick={() => setBlackComponentOpen((prev) => !prev)}>
                            <GoTimesIcon />
                        </IconButton>
                    </Stack>
                    <Typography color="white" fontWeight="normal" size="b_sm">
                        {description}
                    </Typography>
                </Stack>
                <Container height="100%" display="flex" direction="column">
                    <Textarea
                        className={promptTextarea}
                        name="prompt"
                        placeholder={promptPlaceholder}
                        onChange={(e) => setPrompt(e.target.value)}
                    />
                    <Button
                        href={buttonHref}
                        target="_blank"
                        onClick={() => {
                            sendBiClickEvent();
                            setBlackComponentOpen((prev: boolean) => !prev);
                        }}
                        fullWidth
                        marginTop="4"
                    >
                        {cta}
                        <GoArrowRightIcon />
                    </Button>
                </Container>
            </Container>
        </Stack>
    );
};

export default BasicAiGigCard;
